import { ReactNode, useMemo, useRef, useState } from 'react';

import { types } from '@boss/boss-client';
import { useGeoLocation } from '@boss/hooks';
import { SearchDropdown } from '@boss/ui';
import { Coordinates } from '@boss/utils';

import { sortAfhaalpunten } from './sort-afhaalpunten';

type Props = {
  stores: types.Store[];
  favouriteShopId?: string;
  value?: string | null;
  onChange: (store: types.Store) => void;
  searchLabel?: string;
  selectLabel?: string;
  className?: string;
  dropdownClassName?: string;
  error?: boolean;
  renderStoreStatus?: (store: types.Store) => ReactNode;
};

/**
 * Shared afhaalpunt dropdown: pins the favourite shop first, sorts the rest via
 * the CL-1 cascade (`sortAfhaalpunten`) and emits the full selected `types.Store`
 * so consumers get both the preferred shop id and the `coloradepotid` warehouse
 * code. Geolocation is requested lazily on the first open — never on mount, so
 * customers on default "Levering" are not prompted.
 */
const AfhaalpuntDropdown = ({
  stores,
  favouriteShopId,
  value,
  onChange,
  searchLabel,
  selectLabel,
  className,
  dropdownClassName,
  error,
  renderStoreStatus,
}: Props) => {
  const { fetchGeolocation } = useGeoLocation();
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const geolocationRequested = useRef(false);

  const storesById = useMemo(() => new Map(stores.map(store => [store.id, store])), [stores]);
  const options = useMemo(
    () =>
      sortAfhaalpunten(stores, { favouriteShopId, coordinates }).map(store => ({ value: store.id, label: store.name })),
    [stores, favouriteShopId, coordinates],
  );

  const handleOpen = () => {
    if (geolocationRequested.current) {
      return;
    }

    geolocationRequested.current = true;

    const resolveCoordinates = async () => {
      const resolvedCoordinates = await fetchGeolocation();

      if (resolvedCoordinates) {
        setCoordinates(resolvedCoordinates);
      }
    };

    resolveCoordinates();
  };

  const handleChange = (storeId: string) => {
    const store = storesById.get(storeId);

    if (store) {
      onChange(store);
    }
  };

  const renderOptionLabel = renderStoreStatus
    ? (option: { label: string; value: string }): ReactNode => {
        const store = storesById.get(option.value);

        if (!store) {
          return option.label;
        }

        return (
          <>
            {option.label}
            {renderStoreStatus(store)}
          </>
        );
      }
    : undefined;

  return (
    <SearchDropdown
      className={className}
      dropdownClassName={dropdownClassName}
      error={error}
      onChange={handleChange}
      onOpen={handleOpen}
      options={options}
      renderOptionLabel={renderOptionLabel}
      searchLabel={searchLabel}
      selectLabel={selectLabel}
      value={value}
    />
  );
};

export default AfhaalpuntDropdown;
